// WebhooksTab — per-tenant, per-workspace webhook config CRUD.
//
// Backend: WebhookConfigService (Go, go_app/internal/webhook_config + gRPC-gateway).
// Routes consumed here:
//   GET    /api/v1/webhooks/configs
//   POST   /api/v1/webhooks/configs            (inside WebhookConfigDialog)
//   PUT    /api/v1/webhooks/configs/{id}       (inside WebhookConfigDialog)
//   PUT    /api/v1/webhooks/configs/{id}/active
//   DELETE /api/v1/webhooks/configs/{id}
import React, {Component} from 'react';
import {Add, Webhook, Edit, Delete, PauseCircle, PlayCircle} from '@mui/icons-material';
import {
  Alert, Button, Chip, CircularProgress, IconButton, Snackbar, Tooltip,
} from '@mui/material';

import Api from '../../api';
import Table from '../../components/Table';
import {UI_API_URL_PATH_NEW} from '../../lib/constant';
import WebhookConfigDialog from './WebhookConfigDialog';

const BASE = `${UI_API_URL_PATH_NEW}/api/v1/webhooks/configs`;

// Wire enum strings from the gRPC-gateway JSON to human labels. Keeps the
// table cells short without wiring a fresh i18n layer.
const TYPE_LABEL = {
  'WEBHOOK_TYPE_SMS_DELIVERY': 'SMS Delivery',
};
const METHOD_LABEL = {
  'WEBHOOK_METHOD_POST': 'POST',
  'WEBHOOK_METHOD_GET': 'GET',
};

export default class WebhooksTab extends Component {
  state = {
    configs: [],
    loading: true,
    dialogOpen: false,
    editingConfig: null,          // null = create mode; object = edit mode
    snackbar: {open: false, message: '', severity: 'success'},
  }

  componentDidMount() {
    this.fetch();
  }

  fetch = () => {
    this.setState({loading: true});
    Api.get(BASE, true, {includeInactive: true})
      .then(resp => {
        // Response shape: { success: true, data: [...], total_count, next_page_token }
        const list = resp?.data?.data || [];
        this.setState({configs: list, loading: false});
      })
      .catch(err => {
        console.error('Failed to fetch webhook configs:', err);
        this.setState({configs: [], loading: false});
      });
  }

  openCreate = () => this.setState({dialogOpen: true, editingConfig: null})
  openEdit = (cfg) => this.setState({dialogOpen: true, editingConfig: cfg})
  closeDialog = () => this.setState({dialogOpen: false, editingConfig: null})

  toast = (message, severity = 'success') =>
    this.setState({snackbar: {open: true, message, severity}})

  onSaved = (msg) => {
    this.closeDialog();
    this.toast(msg, 'success');
    this.fetch();
  }

  toggleActive = (cfg) => {
    const next = !cfg.active;
    Api.put(`${BASE}/${cfg.id}/active`, {active: next})
      .then(() => {
        this.toast(next ? 'Webhook resumed' : 'Webhook paused', 'success');
        this.fetch();
      })
      .catch(err => {
        const msg = err?.response?.data?.message || 'Failed to change active state';
        this.toast(msg, 'error');
      });
  }

  remove = (cfg) => {
    const label = cfg.name || cfg.id;
    if (!window.confirm(`Delete webhook "${label}"? This cannot be undone.`)) return;
    Api.delete(`${BASE}/${cfg.id}`)
      .then(() => {
        this.toast('Webhook deleted', 'success');
        this.fetch();
      })
      .catch(err => {
        const msg = err?.response?.data?.message || 'Failed to delete';
        this.toast(msg, 'error');
      });
  }

  render() {
    const {configs, loading, dialogOpen, editingConfig, snackbar} = this.state;
    // Active webhooks first; paused sink to the bottom so they don't clutter the
    // active set but stay visible for resume/delete.
    const sortedConfigs = [...configs].sort((a, b) => Number(!!b.active) - Number(!!a.active));
    // Dim non-Status/Actions cells on paused rows so the row reads as "faded out"
    // while leaving the Status chip + action buttons fully legible.
    const dim = (row, content) => (
      <span style={{opacity: row.active ? 1 : 0.45}}>{content}</span>
    );

    return (
      <div>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16}}>
          <h4 style={{color: 'black', fontWeight: 300, fontSize: 18, margin: 0}}>
            Webhooks deliver SMS events (delivery reports) to a URL you control.
          </h4>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={this.openCreate}
            size="small"
          >
            Create Webhook
          </Button>
        </div>

        {loading ? <CircularProgress /> : (
          configs.length > 0 ? (
            <Table
              column={[
                {name: 'Name', value: 'name', cell: (val, row) => dim(row, val || <em>unnamed</em>)},
                {name: 'Type', value: 'webhookType', cell: (val, row) => dim(row, <Chip size="small" label={TYPE_LABEL[val] || val} variant="outlined" />)},
                {name: 'Workspace', value: 'workspaceId', cell: (val, row) => dim(row, val || '-')},
                {name: 'URL', value: 'targetUrl', cell: (val, row) => dim(row, (
                  <code style={{fontSize: 12, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block'}}>
                    {val}
                  </code>
                ))},
                {name: 'Method', value: 'method', cell: (val, row) => dim(row, METHOD_LABEL[val] || val)},
                {name: 'Status', value: 'active', cell: val => val
                  ? <Chip size="small" label="Active" color="success" />
                  : <Chip size="small" label="Paused" color="default" />
                },
                {name: 'Created', value: 'createdAt', cell: (val, row) => dim(row, val ? new Date(val).toLocaleDateString() : '-')},
                {name: 'Actions', value: 'id', cell: (val, row) => (
                  <div style={{display: 'flex', gap: 4}}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => this.openEdit(row)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={row.active ? 'Pause' : 'Resume'}>
                      <IconButton size="small" onClick={() => this.toggleActive(row)}>
                        {row.active ? <PauseCircle fontSize="small" /> : <PlayCircle fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => this.remove(row)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                )},
              ]}
              data={sortedConfigs}
            />
          ) : (
            <div style={{textAlign: 'center', padding: 40}}>
              <Webhook style={{fontSize: 48, color: '#ccc'}} />
              <p>No webhooks configured. Create one to receive SMS delivery events.</p>
            </div>
          )
        )}

        {dialogOpen && (
          <WebhookConfigDialog
            config={editingConfig}
            onClose={this.closeDialog}
            onSaved={this.onSaved}
            onError={(msg) => this.toast(msg, 'error')}
          />
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => this.setState({snackbar: {...snackbar, open: false}})}
          anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
        >
          <Alert severity={snackbar.severity} onClose={() => this.setState({snackbar: {...snackbar, open: false}})}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    );
  }
}
